// src/lib/tutor-tools/plan-check-summary.ts
//
// The one line the chat shows for a check_plan_time result. It used to say
// "Checked the plan against 20 minutes a day: 1 doesn't fit" — QA: the finished
// plan had already left the full SAT out, yet the card still read as if the
// plan didn't fit, and never said WHICH activity. So it names each activity
// that doesn't fit, with its real length, and any session that runs over.
// Client-safe: ToolCard renders it.

import { formatMinutes } from '@/lib/school/assignment-length'

type Activity = { label?: string; minutes?: number; fits?: boolean; sittings?: number }
type Result = {
  budgetMinutes?: number
  activities?: Activity[]
  overBudgetSessions?: Array<{ session: string; minutes: number }>
}

const say = (m: number | undefined) => (typeof m === 'number' ? formatMinutes(Math.round(m)) : '')

export function planCheckSummary(r: Result): string {
  const budget = r.budgetMinutes
  const acts = r.activities ?? []
  const head = `Checked ${acts.length} ${acts.length === 1 ? 'activity' : 'activities'} against ${budget ?? '?'} minutes a day.`
  const misfits = acts.filter((a) => a.fits === false)
  const over = r.overBudgetSessions ?? []
  if (misfits.length === 0 && over.length === 0) {
    const split = acts.filter((a) => a.fits && a.sittings && a.sittings > 1)
    return split.length
      ? `${head} All fit (${split.map((a) => `${a.label ?? 'one'} over ${a.sittings} sessions`).join(', ')}).`
      : `${head} All fit.`
  }
  const parts: string[] = []
  if (misfits.length) {
    parts.push(
      `Doesn't fit a ${budget ?? '?'}-minute day: ${misfits
        .map((a) => `${a.label ?? 'an activity'}${a.minutes ? ` (${say(a.minutes)})` : ''}`)
        .join(', ')}.`,
    )
  }
  if (over.length) {
    parts.push(
      `Runs over: ${over.map((s) => `the ${s.session} session (${say(s.minutes)})`).join(', ')}.`,
    )
  }
  return `${head} ${parts.join(' ')}`
}

type FitResult = Result & { timeMultiplier?: number }

/**
 * The headline of the passing plan check's card: "Fits your 20 minutes a day".
 * The words carry the verdict, so the card's colour is never the only signal.
 */
export function planFitHeadline(r: FitResult): string {
  const budget = r.budgetMinutes
  return typeof budget === 'number'
    ? `Fits your ${formatMinutes(Math.round(budget))} a day`
    : 'Fits your daily time'
}

/**
 * The line under it: what was checked, which activities are spread over
 * several sessions, and whether lengths include extended time.
 */
export function planFitDetail(r: FitResult): string {
  const acts = r.activities ?? []
  const parts = [
    `Every activity in this plan was checked: ${acts.length} ${
      acts.length === 1 ? 'activity' : 'activities'
    }, all fit.`,
  ]
  const split = acts.filter((a) => a.fits && a.sittings && a.sittings > 1)
  if (split.length) {
    parts.push(
      `${split.map((a) => `${a.label ?? 'One'} is spread over ${a.sittings} sessions`).join('; ')}.`,
    )
  }
  if (typeof r.timeMultiplier === 'number' && r.timeMultiplier > 1) {
    parts.push(`Lengths include your ${r.timeMultiplier}× extended time.`)
  }
  return parts.join(' ')
}
